import DOMPurify from 'dompurify';
import { api } from '../../core/network/api';
import { t } from '../i18n';

export interface LegalPage {
  id: string;
  slug: string;
  title: string;
  content: string;
  updatedAt: string;
}

export interface CompanyInfo {
  name: string;
  registrationNumber: string;
  address: string;
  email: string;
  jurisdiction: string;
  country: string;
  gameName: string;
}

/** The directory entry for a document — enough to render a link, not the body. */
export type LegalPageMeta = Pick<LegalPage, 'slug' | 'title'>;

/** GET /api/cms/legal/:slug — an active legal document from the core CMS (admin-managed). */
export const fetchLegalPage = (slug: string) => api.get<LegalPage>(`/cms/legal/${slug}`);

/** GET /api/legal/pages — every ACTIVE legal document (admin-managed), for menus. */
export const fetchLegalPages = () => api.get<LegalPageMeta[]>('/legal/pages');

/** The three founding papers: slug, the English title the seed gives them
 *  (backend/src/game/legal/legal.constants.ts) and the i18n key the web footer
 *  uses, so the Papers shelf and the footer never name one document two
 *  different ways (in Polish the footer says "Regulamin"). LegalPage rows have
 *  no locale column, so the key is the only translation these titles get. */
export const FOUNDING_PAPERS = [
  { slug: 'terms-of-service', seedTitle: 'Terms of Service', key: 'landing.footer.terms' },
  { slug: 'privacy-policy', seedTitle: 'Privacy Policy', key: 'landing.footer.privacy' },
  { slug: 'cookie-policy', seedTitle: 'Cookie Policy', key: 'landing.footer.cookies' },
] as const;

/** Whether `doc` is a founding paper still carrying its seed title, i.e. the
 *  admins have not renamed it. Only then is the translated title right: once an
 *  admin renames "Cookie Policy" to "Refund Policy", the CMS title must win. */
export function keepsSeedTitle(doc: LegalPageMeta): boolean {
  const paper = FOUNDING_PAPERS.find((p) => p.slug === doc.slug);
  return !!paper && doc.title.trim().toLowerCase() === paper.seedTitle.toLowerCase();
}

/** Display title for a legal doc: a founding paper under its seed title
 *  translates via the footer's key; anything else shows its CMS title as
 *  written (admin-authored, single-language by design). */
export function legalDocTitle(doc: LegalPageMeta): string {
  const paper = FOUNDING_PAPERS.find((p) => p.slug === doc.slug);
  return paper && keepsSeedTitle(doc) ? t(paper.key) : doc.title;
}

/** Menu order: the founding papers in their familiar footer order, then
 *  anything the admins added, alphabetically by slug. */
export function sortLegalDocs<T extends LegalPageMeta>(docs: T[]): T[] {
  const order: readonly string[] = FOUNDING_PAPERS.map((p) => p.slug);
  const rank = (slug: string) => {
    const i = order.indexOf(slug);
    return i === -1 ? order.length : i;
  };
  return [...docs].sort((a, b) => rank(a.slug) - rank(b.slug) || a.slug.localeCompare(b.slug));
}

/** GET /api/legal/company — public legal-entity info (admin-managed `company.*` settings). */
export const fetchCompanyInfo = () => api.get<CompanyInfo>('/legal/company');

/**
 * Substitute `{{company.*}}` / `{{game.name}}` / `{{year}}` placeholders in
 * admin-authored legal HTML, so the documents track the legal entity
 * configured in Admin → Game Settings without copy edits.
 */
export function fillCompanyVars(html: string, c: CompanyInfo | null): string {
  const vars: Record<string, string> = {
    'company.name': c?.name || 'the Company',
    'company.registration': c?.registrationNumber || '—',
    'company.address': c?.address || '—',
    'company.email': c?.email || 'support@gamepreview.it.com',
    'company.jurisdiction': c?.jurisdiction || c?.country || '—',
    'company.country': c?.country || '—',
    'game.name': c?.gameName || 'Fimbulwake',
    year: String(new Date().getFullYear()),
  };
  const filled = html.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (m, key: string) => vars[key] ?? m);
  return DOMPurify.sanitize(filled, { ADD_ATTR: ['data-legal'] });
}
